import React, { useEffect, useMemo, useState } from 'react';
import {
  Paper, Table, TableHead, TableRow, TableCell, Button, TableBody, Typography, Box,
  IconButton, TextField, Grid, useMediaQuery, useTheme, CircularProgress, Snackbar, Alert,
  TableContainer, TablePagination, InputAdornment, Chip, Checkbox, Popover, List,
  ListItem, ListItemButton, Divider, ListItemText, TableSortLabel
} from '@mui/material';
import {
  PictureAsPdf as PictureAsPdfIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const PedidosTable = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);
  const [usersData, setUsersData] = useState([]);

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    partners: [],
    fechaDesde: '',
    fechaHasta: ''
  });

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('purchaseOrder');

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Popover de filtros (Partner)
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterSearch, setFilterSearch] = useState('');
  const [currentFilter, setCurrentFilter] = useState(null);

  const isProveedor = !!currentUser?.roles?.includes('Proveedor');

  /* ===========================
     Helpers de normalización
  ============================ */
  const norm = (v) => (v == null ? '' : String(v).trim().toUpperCase());
  const strip0 = (v) => norm(v).replace(/^0+/, '');
  const pad10  = (v) => {
    const s = strip0(v);
    return s.length >= 10 ? s : s.padStart(10, '0');
  };

  const pickName = (u) =>
    u?.nombre ??
    u?.Nombre ??
    u?.name ??
    u?.Name ??
    u?.razon_social ??
    u?.RazonSocial ??
    u?.razonSocial ??
    '';

  const pickPartner = (u) =>
    u?.partner ??
    u?.Partner ??
    u?.lifnr ??
    u?.LIFNR ??
    u?.supplier ??
    u?.Supplier ??
    '';

  // Mapa robusto <partner variante, nombre>
  const partnerNameMap = useMemo(() => {
    const m = new Map();
    for (const u of (usersData || [])) {
      const raw = pickPartner(u);
      const name = pickName(u) || '';
      if (!raw) continue;

      const k1 = norm(raw);
      const k2 = strip0(raw);
      const k3 = pad10(raw);

      if (k1) m.set(k1, name);
      if (k2) m.set(k2, name);
      if (k3) m.set(k3, name);
    }
    return m;
  }, [usersData]);

  const getUserNameByPartner = (partnerId) => {
    if (!partnerId) return '';
    const k1 = norm(partnerId);
    const k2 = strip0(partnerId);
    const k3 = pad10(partnerId);
    return partnerNameMap.get(k1) || partnerNameMap.get(k2) || partnerNameMap.get(k3) || '';
  };

  /* ===========================
     Carga de usuario + catálogo
  ============================ */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const userResponse = await axios.get('http://172.20.0.11:5000/api/validate-session', { withCredentials: true });
        setCurrentUser(userResponse.data.user);

        const usersResponse = await axios.get('http://172.20.0.11:5000/api/usuarios', { withCredentials: true });
        const list = Array.isArray(usersResponse.data) ? usersResponse.data : [];
        setUsersData(list);
      } catch (err) {
        console.error('Error al cargar usuario/catálogo:', err);
        setError('Error al cargar información de usuario');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ===========================
     Normalizador de filas pedidos
  ============================ */
  const normalizeRows = (rows = []) =>
    rows.map(r => ({
      purchaseOrder: r.purchaseOrder ?? r.PurchaseOrder ?? '',
      companyCode: r.companyCode ?? r.CompanyCode ?? '',
      supplier: r.supplier ?? r.Supplier ?? '',
      currency: r.currency ?? r.DocumentCurrency ?? '',
      totalNetAmount: r.totalNetAmount != null
        ? Number(r.totalNetAmount)
        : (r.TotalNetAmount != null ? Number(r.TotalNetAmount) : null),
      docDate: r.docDate ?? r.DocDate ?? null,
      supplierName:
        r.supplierName ??
        r.SupplierName ??
        r.nombreProveedor ??
        r.NombreProveedor ??
        r.razon_social ??
        r.RazonSocial ??
        null
    }));

  const fetchPedidos = async () => {
    try {
      setLoading(true);

      const lifnrParam = !isProveedor && filters.partners.length === 1 ? filters.partners[0] : undefined;

      const params = {
        page: page + 1,
        pageSize: rowsPerPage,
        q: searchTerm || undefined,
        partners: lifnrParam ? lifnrParam : undefined
      };

      const resp = await axios.get('http://172.20.0.11:5000/api/pedidos', { params, withCredentials: true });

      setTotal(resp.data.total || 0);
      setData(normalizeRows(resp.data.rows || []));
    } catch (err) {
      console.error('Error al cargar pedidos:', err);
      setError('Error al cargar pedidos. Intenta nuevamente.');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPedidos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, JSON.stringify(filters.partners), isProveedor]);

  /* ===========================
     Tabla: ordenar / filtrar
  ============================ */
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const verPDF = (po) => {
    try {
      const url = `http://172.20.0.11:5000/api/pedidos/${po}/pdf`;
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error al abrir PDF:', error);
      setError('Error al visualizar el PDF');
      setSnackbarOpen(true);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({ partners: [], fechaDesde: '', fechaHasta: '' });
    setPage(0);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

  // Partners únicos (en datos ya paginados)
  const getUniquePartnersInData = useMemo(() => {
    if (!data?.length) return [];
    const setP = new Set(data.map(r => String(r.supplier)).filter(Boolean));
    return Array.from(setP);
  }, [data]);

  // Popover filtros
  const handleFilterClick = (event, filterType) => {
    setCurrentFilter(filterType);
    setAnchorEl(event.currentTarget);
    setFilterSearch('');
  };
  const handleFilterClose = () => { setAnchorEl(null); setCurrentFilter(null); setFilterSearch(''); };
  const open = Boolean(anchorEl);
  const popoverId = open ? 'filter-popover' : undefined;

  const handleToggleFilterItem = (value) => {
    const current = [...filters.partners];
    const i = current.indexOf(value);
    if (i === -1) current.push(value); else current.splice(i, 1);
    setFilters({ ...filters, partners: current });
    setPage(0);
  };
  const handleSelectAllFilterItems = () => {
    const all = getUniquePartnersInData;
    setFilters({
      ...filters,
      partners: filters.partners.length === all.length ? [] : all
    });
    setPage(0);
  };

  const safeLower = (s) => (s ? String(s).toLowerCase() : '');

  const filteredPartnerOptions = useMemo(() => {
    return getUniquePartnersInData.filter(p => {
      const name = getUserNameByPartner(p);
      const q = safeLower(filterSearch);
      return safeLower(p).includes(q) || safeLower(name).includes(q);
    });
  }, [getUniquePartnersInData, filterSearch, partnerNameMap]);

  // Filtrado/ordenamiento cliente
  const visibleData = useMemo(() => {
    let rows = [...data];

    if (!isProveedor && filters.partners.length > 1) {
      const setP = new Set(filters.partners.map(String));
      rows = rows.filter(r => setP.has(String(r.supplier)));
    }

    if (filters.fechaDesde) {
      const d = new Date(filters.fechaDesde);
      rows = rows.filter(r => r.docDate ? new Date(r.docDate) >= d : true);
    }
    if (filters.fechaHasta) {
      const d = new Date(filters.fechaHasta + 'T23:59:59');
      rows = rows.filter(r => r.docDate ? new Date(r.docDate) <= d : true);
    }

    rows.sort((a, b) => {
      const dir = order === 'asc' ? 1 : -1;
      const av = a[orderBy];
      const bv = b[orderBy];
      if (av == null && bv == null) return 0;
      if (av == null) return -1 * dir;
      if (bv == null) return 1 * dir;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });

    return rows;
  }, [data, filters.partners, filters.fechaDesde, filters.fechaHasta, isProveedor, order, orderBy]);

  /* ===========================
     Render
  ============================ */
  return (
    <Box sx={{ p: isMobile ? 1 : 2, maxWidth: '1200px', m: '0 auto' }}>
      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', mb: 2, gap: 1 }}>
        <Typography variant="h6" component="h1" sx={{ fontWeight: 'bold' }}>
          Pedidos de Compra
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate('/')}
          startIcon={<ArrowBackIcon fontSize="small" />}
          sx={{ width: isMobile ? '100%' : 'auto', fontSize: '0.8rem' }}
        >
          Volver a factura
        </Button>
      </Box>

      <Paper elevation={1} sx={{ p: 2, mb: 1.5, borderRadius: 3, backgroundColor: '#fff', boxShadow: '0 10px 24px rgba(2, 6, 23, 0.06)', border: '1px solid #eef2f7' }}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={5} md={3}>
            <TextField
              fullWidth size="small" label="Buscar"
              value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>),
                endAdornment: searchTerm && (
                  <IconButton size="small" onClick={() => setSearchTerm('')} sx={{ mr: -1 }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ),
                sx: { height: '35px' }
              }}
              sx={{
                '& .MuiOutlinedInput-root': { height: '35px' },
                '& .MuiInputLabel-outlined': { transform: 'translate(14px, 14px) scale(1)' },
                '& .MuiInputLabel-outlined.MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.75)' }
              }}
            />
          </Grid>

          {!isProveedor && (
            <Grid item xs={6} sm={3} md={2}>
              <Button
                fullWidth variant="outlined" size="small"
                onClick={(e) => handleFilterClick(e, 'partner')}
                sx={{ height: '35px', justifyContent: 'space-between', textTransform: 'none' }}
                endIcon={<FilterListIcon fontSize="small" />}
              >
                Partner
                {filters.partners.length > 0 && <Chip label={filters.partners.length} size="small" sx={{ ml: 1 }} />}
              </Button>
            </Grid>
          )}

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth size="small" label="Fecha desde" type="date" InputLabelProps={{ shrink: true }}
              value={filters.fechaDesde}
              onChange={(e) => { setFilters({ ...filters, fechaDesde: e.target.value }); setPage(0); }}
              sx={{ height: '35px', '& .MuiInputBase-root': { height: '35px' }, '& .MuiInputBase-input': { fontSize: '0.8rem', p: '8.5px 14px' } }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth size="small" label="Fecha hasta" type="date" InputLabelProps={{ shrink: true }}
              value={filters.fechaHasta}
              onChange={(e) => { setFilters({ ...filters, fechaHasta: e.target.value }); setPage(0); }}
              sx={{ height: '35px', '& .MuiInputBase-root': { height: '35px' }, '& .MuiInputBase-input': { fontSize: '0.8rem', p: '8.5px 14px' } }}
            />
          </Grid>

          <Grid item xs={6} sm={2} md={1}>
            <Button
              fullWidth size="small" variant="outlined" color="error" onClick={handleClearFilters}
              startIcon={<ClearIcon fontSize="small" />} sx={{ height: '35px', fontSize: '0.7rem' }}
            >
              Limpiar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Popover
        id={popoverId} open={open} anchorEl={anchorEl} onClose={handleFilterClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { width: 300, maxHeight: 400, p: 1 } }}
      >
        <TextField
          fullWidth size="small" placeholder="Buscar partner..."
          value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 1 }}
        />
        <List dense sx={{ width: '100%', maxHeight: 300, overflow: 'auto' }}>
          <ListItemButton onClick={handleSelectAllFilterItems}>
            <Checkbox
              edge="start"
              checked={filters.partners.length === getUniquePartnersInData.length && getUniquePartnersInData.length > 0}
              indeterminate={filters.partners.length > 0 && filters.partners.length < getUniquePartnersInData.length}
              tabIndex={-1} disableRipple
            />
            <ListItemText primary="Seleccionar todos" />
          </ListItemButton>
          <Divider />
          {filteredPartnerOptions.map((value) => {
            const selected = filters.partners.includes(value);
            return (
              <ListItem key={value} disablePadding>
                <ListItemButton onClick={() => handleToggleFilterItem(value)} dense>
                  <Checkbox edge="start" checked={selected} tabIndex={-1} disableRipple />
                  <ListItemText
                    primary={value}
                    secondary={getUserNameByPartner(value)}
                    sx={{ '& .MuiListItemText-secondary': { fontSize: '0.75rem', color: 'text.secondary' } }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Popover>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
      ) : !visibleData?.length ? (
        <Paper elevation={1} sx={{ p: 2, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <Typography variant="body2">No hay pedidos disponibles</Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} elevation={1} sx={{ maxHeight: '500px', overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f3f4f6',
                  '& th': { fontWeight: 'bold', fontSize: '0.75rem', py: 1, borderBottom: '1px solid #bdbdbd' } }}>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'purchaseOrder'}
                      direction={orderBy === 'purchaseOrder' ? order : 'asc'}
                      onClick={() => handleRequestSort('purchaseOrder')}
                    >
                      Pedido
                    </TableSortLabel>
                  </TableCell>
                  {!isMobile && (
                    <>
                      <TableCell>Partner</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'docDate'}
                          direction={orderBy === 'docDate' ? order : 'asc'}
                          onClick={() => handleRequestSort('docDate')}
                        >
                          Fecha
                        </TableSortLabel>
                      </TableCell>
                    </>
                  )}
                  <TableCell>Moneda</TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'totalNetAmount'}
                      direction={orderBy === 'totalNetAmount' ? order : 'asc'}
                      onClick={() => handleRequestSort('totalNetAmount')}
                    >
                      Importe
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleData.map((row) => {
                  const nameFromMap = getUserNameByPartner(row.supplier);
                  const shownName = row.supplierName || nameFromMap || '-';
                  return (
                    <TableRow key={row.purchaseOrder || Math.random()} hover sx={{ '& td': { fontSize: '0.75rem', py: 0.5 } }}>
                      <TableCell>{row.purchaseOrder}</TableCell>
                      {!isMobile && (
                        <>
                          <TableCell>
                            <Chip label={row.supplier} size="small" sx={{ fontSize: '0.7rem', height: '24px' }} />
                          </TableCell>
                          <TableCell>{shownName}</TableCell>
                          <TableCell>{row.docDate ? new Date(row.docDate).toLocaleDateString('es-MX') : '-'}</TableCell>
                        </>
                      )}
                      <TableCell>{row.currency || '-'}</TableCell>
                      <TableCell align="right">
                        {row.totalNetAmount != null
                          ? Number(row.totalNetAmount).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); verPDF(row.purchaseOrder); }}
                            aria-label="Ver PDF" color="error"
                            sx={{ p: 0.5, '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' } }}
                          >
                            <PictureAsPdfIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            labelRowsPerPage="Filas:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{ mt: 1, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' } }}
          />
        </>
      )}

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity="error" sx={{ width: '100%', fontSize: '0.8rem' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PedidosTable;
